import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { ThemeToggle, LangToggle } from "@/components/Toggles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Droplets, Search, Wine, Package, Hash, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = { requested: "#F59E0B", confirmed: "#3B82F6", pending_payment: "#F59E0B", paid: "#059669", fulfilled: "#059669" };

export default function OrderLookup() {
  const { code } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();
  const [input, setInput] = useState(code || "");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (c) => {
    const q = (c || input).toUpperCase().trim();
    if (!q) return;
    setLoading(true); setOrder(null);
    try {
      const { data } = await api.get(`/public/store-order/${q}`);
      setOrder(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("order_not_found")); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (code) lookup(code); /* eslint-disable-next-line */ }, [code]);

  return (
    <div className="min-h-screen bg-background grain-bg">
      <header className="flex items-center justify-between border-b border-border px-4 py-4 sm:px-6">
        <button onClick={() => navigate("/")} className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Droplets className="h-5 w-5" /></div>
          <span className="font-heading font-bold">e-olive Albania</span>
        </button>
        <div className="flex items-center gap-2"><LangToggle /><ThemeToggle /></div>
      </header>

      <div className="mx-auto max-w-lg px-4 py-10" data-testid="order-lookup-page">
        <h1 className="font-heading text-2xl font-bold sm:text-3xl">{t("lookup_title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("lookup_sub")}</p>

        <div className="mt-6 flex items-center gap-2">
          <div className="relative flex-1">
            <Hash className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="lookup-input" value={input} onChange={(e) => setInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && lookup()} placeholder={t("enter_code")} className="pl-9 font-mono" />
          </div>
          <Button data-testid="lookup-btn" onClick={() => lookup()} disabled={loading || !input} className="rounded-lg bg-primary hover:bg-olive-hover">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </div>

        {order && (
          <div className="mt-8 rounded-2xl border border-border bg-card p-6" data-testid="lookup-result">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-olive-light px-3 py-1 font-mono text-sm font-semibold text-olive"><Hash className="h-3.5 w-3.5" />{order.order_code}</span>
              <span className="rounded-full px-3 py-1 text-sm font-semibold" style={{ background: `${STATUS_COLORS[order.status] || "#888"}1a`, color: STATUS_COLORS[order.status] || "#888" }}>{t(order.status)}</span>
            </div>
            <div className="mt-5 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-glow text-amber-deep"><Wine className="h-6 w-6" /></div>
              <div>
                <p className="font-heading text-lg font-bold">{order.quantity}× {order.product_name}</p>
                <p className="text-sm text-muted-foreground">€{Number(order.amount || 0).toFixed(2)} · {order.mill_name}</p>
              </div>
            </div>
            <p className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground"><Package className="h-3.5 w-3.5" /> {order.customer_name}</p>
          </div>
        )}
      </div>
    </div>
  );
}
